import React, { useMemo, useRef } from 'react';

import {
  WidgetCnt,
  WidgetHeading,
} from 'pages/Dashboards/Widgets/widgets.style';
import {
  Bar,
  BarChart,
  CartesianGrid,
  Cell,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import { stringToHslColor } from 'helper/stringToColor';
import TasksByAssigneeEmptyState from 'pages/Widgets/TasksByAssignee/EmptyRecords/TaskListWidgetEmptyState.cmp';
import WidgetActions from 'pages/Dashboards/Widgets/widgetActions';
import { IWidgetData } from 'pages/Dashboards/interfaces';
import { exportToPng } from 'utils/globalFunction';
import { useLoadWidget } from 'pages/Dashboards/Widgets/useLoadWidget';
import WidgetAdvanceFilter from 'pages/Dashboards/Widgets/WidgetAdvanceFilter/widgetAdvanceFilter';
import WithErrorState from 'pages/Dashboards/Widgets/errorStateCmp/errorState';
import WithLoader from 'pages/Dashboards/Widgets/withLoader';
import {
  useViewSubmissions,
  useViewTasks,
} from 'pages/Dashboards/hooks/useWidgets';
import { useDispatch } from 'react-redux';

interface IBarChartWidgetProps {
  widgetId: string;
  editable?: boolean;
  globalFilters?: any;
}
function BarChartWidget({
  widgetId,
  editable,
  globalFilters,
}: IBarChartWidgetProps) {
  const chartRef: any = useRef(null);
  const { parsedData, getWidgetQuery, widget } = useLoadWidget(
    widgetId,
    globalFilters,
  );
  const dispatch = useDispatch();
  const exportChartToImage = () => {
    exportToPng(chartRef.current, widget?.title);
  };
  const loading = getWidgetQuery.isFetching || false;
  const error = getWidgetQuery.error;
  const { handleViewTasks } = useViewTasks();
  const { handleViewSubmissions } = useViewSubmissions();

  const handleBarClick = (e: any) => {
    if (widget?.entity === 'task') {
      handleViewTasks(
        widget,
        e.metadata,
        getWidgetQuery.refetch,
        globalFilters,
      );
    }

    if (widget?.entity === 'scheduledTask') {
      handleViewTasks(
        widget,
        e.metadata,
        getWidgetQuery.refetch,
        globalFilters,
        true,
      );
    }

    if (widget?.entity == 'submission') {
      handleViewSubmissions(
        widget,
        e.metadata,
        getWidgetQuery.refetch,
        globalFilters,
      );
    }
  };
  const CustomizedAxisTick = ({ x, y, stroke, payload }) => {
    const extractFirstWord = payload.value.split(' ')[0];
    const truncatedValue =
      payload.value.length > 20
        ? payload.value.substring(0, 20) + '...'
        : payload.value;

    return (
      <g transform={`translate(${x},${y})`}>
        <text
          x={0}
          y={0}
          dy={16}
          textAnchor="end"
          fill="#000"
          transform="rotate(-35)"
        >
          {truncatedValue}
        </text>
      </g>
    );
  };

  const isPercentage = useMemo(() => {
    return widget?.config?.variable?.includes('percentage');
  }, [widget]);

  return (
    <WidgetCnt ref={chartRef}>
      <WithLoader isLoading={loading}>
        <WithErrorState
          type={'bar'}
          error={error}
          reload={() => getWidgetQuery.refetch()}
        >
          <WidgetHeading>
            {widget?.title}
            <div className={'widgetActionsCnt'}>
              <WidgetAdvanceFilter
                widgetId={widgetId}
                globalFilters={globalFilters}
              />{' '}
              <WidgetActions
                exportChartToImage={exportChartToImage}
                widgetId={widgetId}
                editable={editable}
              />
            </div>
          </WidgetHeading>
          {parsedData?.length ? (
            <ResponsiveContainer
              minWidth={parsedData.length * 40}
              height="100%"
            >
              <BarChart
                style={{ fontSize: 12 }}
                data={parsedData}
                margin={{
                  top: 20,
                  right: 0,
                  left: -20,
                  bottom: 70,
                }}
              >
                <CartesianGrid
                  strokeDasharray="0"
                  vertical={false}
                  stroke={'#EEEEEE'}
                />
                <XAxis
                  dataKey="valueX"
                  tickLine={false}
                  axisLine={false}
                  color={'red'}
                  angle={-45}
                  tick={(props) => <CustomizedAxisTick {...props} />}
                  interval={0}
                />
                <YAxis
                  type={'number'}
                  domain={[0, 'auto']}
                  tick={{ fill: '#212121', fontSize: 12, fontWeight: 500 }}
                  tickCount={5}
                  tickLine={false}
                  axisLine={false}
                  tickFormatter={(tick) => `${tick}${isPercentage ? '%' : ''}`}
                />
                <Tooltip
                  formatter={(value) => [`${value}${isPercentage ? '%' : ''}`]}
                />
                <Bar
                  onClick={(e) => handleBarClick(e)}
                  dataKey="valueY"
                  fill="#8884d8"
                  label={{
                    position: 'top',
                    ...(isPercentage && {
                      formatter: (value) => `${value}%`,
                    }),
                  }}
                  barSize={40}
                  maxBarSize={40}
                  radius={[5, 5, 0, 0]}
                >
                  {parsedData?.map((entry: IWidgetData, index: number) => (
                    <Cell
                      key={`cell-${index}`}
                      fill={stringToHslColor(entry?.valueX, '66', '67')}
                    />
                  ))}
                </Bar>
                {/*<Bar dataKey="uv" fill="#82ca9d" />*/}
              </BarChart>
            </ResponsiveContainer>
          ) : (
            <TasksByAssigneeEmptyState />
          )}
        </WithErrorState>
      </WithLoader>
    </WidgetCnt>
  );
}

export default BarChartWidget;
